import { EventType } from "@/analytics/analytics-events";
import {
  ChangeScanColorsProperties,
  ChangeScanMarkersVisibilityModeProperties,
  ToggleConnectionLinesVisibilityProperties,
  ToggleQualityColorCodingProperties,
} from "@/analytics/staging-area-workflows-events-properties";
import { HelpPopover } from "@/components/ui/help-popover";
import {
  selectQualityColorCoding,
  selectScanColoring,
  selectScanMarkersVisibilityMode,
  selectShowConnectionLines,
  selectShowDiscardedConnectionLines,
} from "@/data-preparation-tool/store/data-preparation-view-options/data-preparation-view-options-selectors";
import {
  ScanColoring,
  ScanMarkersVisibilityMode,
  setQualityColorCoding,
  setRegistrationEdgeType,
  setScanColoring,
  setScanMarkersVisibilityMode,
  setShowConnectionLines,
  setShowDiscardedConnectionLines,
} from "@/data-preparation-tool/store/data-preparation-view-options/data-preparation-view-options-slice";
import { selectRegistrationEdgeType } from "@/data-preparation-tool/store/revision/revision-edge-selectors";
import { selectIsAnyEditModeEnabled } from "@/data-preparation-tool/store/user-edits/data-preparation-user-edit-selectors";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import {
  FaroMenu,
  FaroMenuProps,
  FaroRadio,
  FaroRadioGroup,
  FaroSwitch,
  FaroText,
  useToast,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { validateEnumValue } from "@faro-lotv/foundation";
import { RegistrationEdgeType } from "@faro-lotv/service-wires";
import { Stack } from "@mui/material";
import { type JSX } from "react";

type DataPreparationViewSettingsMenuProps = Pick<
  FaroMenuProps,
  "open" | "anchorEl" | "onClose"
>;

/** @returns the popover menu containing the data-preparation view settings */
export function DataPreparationViewSettingsMenu({
  open,
  anchorEl,
  onClose,
}: DataPreparationViewSettingsMenuProps): JSX.Element {
  const isRegistrationDev = useAppSelector(
    selectHasFeature(Features.RegistrationDev),
  );
  const showDiscardedConnectionLines = useAppSelector(
    selectShowDiscardedConnectionLines,
  );

  const isEditModeEnabled = useAppSelector(selectIsAnyEditModeEnabled);

  const showColorModeViewSettings = isRegistrationDev || isEditModeEnabled;

  const dispatch = useAppDispatch();

  return (
    <FaroMenu
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: -2,
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      onClose={onClose}
      dark
    >
      <Stack sx={{ gap: 1, p: 1, minWidth: "291px" }}>
        <FaroText
          dark
          variant="heading12"
          sx={{ textTransform: "uppercase", fontSize: "10px", pb: 1 }}
        >
          Registration View Settings
        </FaroText>
        {showColorModeViewSettings && (
          <>
            <Stack
              direction="row"
              sx={{ justifyContent: "space-between", alignItems: "center" }}
            >
              <FaroText dark variant="heading12">
                Color mode
              </FaroText>

              <HelpPopover
                title="Color mode"
                description="Configure in which colors captures will be displayed in this view. Color by capture will display each capture in a different color. Original color preview gives you an early impression of the original capture colors while the preview is not color balanced yet."
              />
            </Stack>
            <ScanColoringRadio />
          </>
        )}
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <FaroText dark variant="heading12">
            Capture markers
          </FaroText>

          <HelpPopover
            title="Capture markers"
            description="Controls the visibility of capture markers. Choose to always show them, show them only for visible captures, or hide them completely."
          />
        </Stack>
        <ScanMarkersVisibilityRadio />
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <FaroText dark variant="heading12">
            Registration connections
          </FaroText>

          <HelpPopover
            title="Registration connections"
            description="Show connections between captures that have been registered together."
          />
        </Stack>
        <RegistrationRevisionConnectionsGroup />

        {isRegistrationDev && (
          <FaroSwitch
            dark
            checked={showDiscardedConnectionLines}
            label="Show discarded connections (DEV)"
            onToggle={() =>
              dispatch(
                setShowDiscardedConnectionLines(!showDiscardedConnectionLines),
              )
            }
          />
        )}
      </Stack>
    </FaroMenu>
  );
}

function isScanColoring(value: string): value is ScanColoring {
  return Object.keys(ScanColoring).includes(value);
}

function isScanMarkersVisibilityMode(
  value: string,
): value is ScanMarkersVisibilityMode {
  return Object.keys(ScanMarkersVisibilityMode).includes(value);
}

function RegistrationRevisionConnectionsGroup(): JSX.Element {
  const isRegistrationDev = useAppSelector(
    selectHasFeature(Features.RegistrationDev),
  );
  const scanMarkersVisibilityMode = useAppSelector(
    selectScanMarkersVisibilityMode,
  );
  const allScanMarkersHidden =
    scanMarkersVisibilityMode === ScanMarkersVisibilityMode.showNone;

  const showConnectionLines =
    useAppSelector(selectShowConnectionLines) &&
    // If all scan markers are hidden then connections will also be hidden
    !allScanMarkersHidden;

  const qualityColorCoding = useAppSelector(selectQualityColorCoding);
  const registrationEdgeType = useAppSelector(selectRegistrationEdgeType);
  const dispatch = useAppDispatch();

  return (
    <>
      <FaroSwitch
        dark
        checked={showConnectionLines}
        inputProps={{ "aria-label": "display connection lines" }}
        label="Show connection lines"
        fullWidth
        onToggle={() => {
          Analytics.track<ToggleConnectionLinesVisibilityProperties>(
            EventType.toggleConnectionLinesVisibility,
            {
              enabling: showConnectionLines,
            },
          );
          dispatch(setShowConnectionLines(!showConnectionLines));
        }}
        disabled={allScanMarkersHidden}
      />
      <FaroSwitch
        dark
        checked={qualityColorCoding}
        inputProps={{ "aria-label": "toggle quality color coding" }}
        label="Quality color coding"
        fullWidth
        onToggle={() => {
          Analytics.track<ToggleQualityColorCodingProperties>(
            EventType.toggleQualityColorCoding,
            {
              enabling: qualityColorCoding,
            },
          );
          dispatch(setQualityColorCoding(!qualityColorCoding));
        }}
        disabled={!showConnectionLines}
      />
      {isRegistrationDev && (
        <Stack
          sx={{
            justifyContent: "space-between",
            ml: 3,
          }}
        >
          <FaroRadioGroup
            value={registrationEdgeType}
            onChange={(_, value) => {
              if (validateEnumValue(value, RegistrationEdgeType)) {
                dispatch(setRegistrationEdgeType(value));
              }
            }}
          >
            <FaroRadio
              dark
              disabled={!showConnectionLines || !qualityColorCoding}
              value={RegistrationEdgeType.global}
              aria-label="display global edges"
              label="Global Results (DEV)"
            />
            <FaroRadio
              dark
              disabled={!showConnectionLines || !qualityColorCoding}
              value={RegistrationEdgeType.local}
              aria-label="display local edges"
              label="Local Results (DEV)"
            />
            <FaroRadio
              dark
              disabled={!showConnectionLines || !qualityColorCoding}
              value={RegistrationEdgeType.preReg}
              aria-label="display pre registration edges"
              label="Pre Registration Results (DEV)"
            />
            <FaroRadio
              dark
              disabled={!showConnectionLines || !qualityColorCoding}
              value={RegistrationEdgeType.slam}
              aria-label="display slam edges"
              label="Slam Results (DEV)"
            />
            <FaroRadio
              dark
              disabled={!showConnectionLines || !qualityColorCoding}
              value={RegistrationEdgeType.candidate}
              aria-label="display candidate edges"
              label="Candidates (DEV)"
            />
          </FaroRadioGroup>
        </Stack>
      )}
    </>
  );
}

function ScanColoringRadio(): JSX.Element {
  const scanColoring = useAppSelector(selectScanColoring);
  const isColorByConnectivityEnabled = useAppSelector(
    selectHasFeature(Features.ColorByConnectivity),
  );
  const isRegistrationDev = useAppSelector(
    selectHasFeature(Features.RegistrationDev),
  );
  const dispatch = useAppDispatch();
  const { openToast } = useToast();

  return (
    <FaroRadioGroup
      value={scanColoring}
      onChange={(_, value) => {
        if (isScanColoring(value)) {
          Analytics.track<ChangeScanColorsProperties>(
            EventType.changeScanColors,
            {
              variant: value,
            },
          );
          dispatch(setScanColoring(value));
          if (value === ScanColoring.originalColor) {
            openToast({
              title: "Original color enabled",
              message:
                "Scans are not color balanced or filtered yet. Display may differ from the final published result.",
              variant: "info",
            });
          }
        }
      }}
    >
      <FaroRadio
        dark
        value={ScanColoring.byScan}
        aria-label="display captures in different colors"
        label="Color by capture"
      />

      <FaroRadio
        dark
        value={ScanColoring.originalColor}
        aria-label="display captures in original color"
        label="Original color"
      />

      {(isRegistrationDev || isColorByConnectivityEnabled) && (
        <FaroRadio
          dark
          value={ScanColoring.byConnectivity}
          aria-label="color by connectivity"
          label="Color by connectivity"
        />
      )}
      {isRegistrationDev && (
        <FaroRadio
          dark
          value={ScanColoring.intensity}
          aria-label="color by intensity"
          label="Color by intensity"
        />
      )}
    </FaroRadioGroup>
  );
}

function ScanMarkersVisibilityRadio(): JSX.Element {
  const scanMarkerVisibilityMode = useAppSelector(
    selectScanMarkersVisibilityMode,
  );
  const dispatch = useAppDispatch();

  return (
    <FaroRadioGroup
      value={scanMarkerVisibilityMode}
      onChange={(_, value) => {
        if (isScanMarkersVisibilityMode(value)) {
          Analytics.track<ChangeScanMarkersVisibilityModeProperties>(
            EventType.changeScanMarkersVisibilityMode,
            {
              variant: value,
            },
          );
          dispatch(setScanMarkersVisibilityMode(value));
        }
      }}
    >
      <FaroRadio
        dark
        value={ScanMarkersVisibilityMode.showAll}
        aria-label="display all markers"
        label="Show all"
      />
      <FaroRadio
        dark
        value={ScanMarkersVisibilityMode.showVisible}
        aria-label="display markers for visible captures"
        label="Show only for visible captures"
      />
      <FaroRadio
        dark
        value={ScanMarkersVisibilityMode.showNone}
        aria-label="hide all markers"
        label="Hide all"
      />
    </FaroRadioGroup>
  );
}
